import { useContext, useMemo } from 'react'
import GeoContext from 'contexts/geoContext'
import useLoyaltyPointsEarnCalculator from './useLoyaltyPointsEarnCalculator'
import {
  generateLoyaltyPointsCalculatorBedbankOptions,
  generateLoyaltyPointsCalculatorBundledFlightOptions,
} from 'loyalty/lib/pointsCalculation/calculatorOptionsGenerators'

interface Params {
  offer: App.BedbankOffer | undefined
  rate: App.BedbankRate | undefined
  nights: number
  /**
   * Flight price for bundled flight calculation.
   * If provided and flights are enabled, flight status credits will be included.
   */
  flightPrice?: number
  skip?: boolean
}

interface BedbankRateStatusCreditsResult {
  statusCredits: number
  isLoading: boolean
  hasError: boolean
}

function useBedbankRateStatusCreditsCalculation(params: Params): BedbankRateStatusCreditsResult {
  const { offer, rate, nights, flightPrice, skip = false } = params
  const { currentRegionCode } = useContext(GeoContext)

  const calculationRequests = useMemo<Array<App.LoyaltyPointsEarnCalculationRequest | undefined>>(() => {
    if (skip || !offer || !rate) {
      return [undefined, undefined]
    }

    const hotelRequest = generateLoyaltyPointsCalculatorBedbankOptions(rate, {
      price: rate.totals.total,
      memberPrice: rate.totals.memberTotal ?? 0,
      propertyFees: rate.totals.propertyFees ?? 0,
      propertyId: offer.property.id,
      offerId: offer.id,
      propertyCountryCode: offer.property.address.countryCode,
      propertyState: offer.property.address.stateProvinceCode,
      currentRegionCode,
      nights,
    })

    const flightRequest = generateLoyaltyPointsCalculatorBundledFlightOptions(offer, {
      flightPrice,
      skip: !flightPrice,
    })

    return [hotelRequest, flightRequest]
  }, [skip, offer, rate, currentRegionCode, nights, flightPrice])

  const { totalPointsCalculation } = useLoyaltyPointsEarnCalculator(calculationRequests, {
    skipAll: skip || !offer || !rate,
  })

  const statusCredits = (totalPointsCalculation.data?.statusCredits ?? 0) + (totalPointsCalculation.data?.bonusStatusCredits ?? 0)
  const isLoading = !!totalPointsCalculation.initial || !!totalPointsCalculation.fetching
  const hasError = !!totalPointsCalculation.error

  return { statusCredits, isLoading, hasError }
}

export default useBedbankRateStatusCreditsCalculation
